import { useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Spinner } from '@fluxora/ui';

import {
  STOREFRONT_OFFER_ARRIVAL_PARAM,
  STOREFRONT_RESUME_ARRIVAL_PARAM,
} from '@rheora/enrollment-contract';

import { env } from '../../../config/env';
import { useFunnelSnapshotQuery } from '../api/funnel.queries';
import { useEnsureSessionQuery } from '../api/serviceability.queries';
import { buildFunnelSeed } from '../services/funnel-rehydrate.service';
import { FunnelProvider } from './FunnelProvider';

/**
 * Start the funnel FRESH before the storefront renders, then mount
 * `FunnelProvider` seeded from the URL alone.
 *
 * ── THE DECISION (2026-09-16) ──────────────────────────────────────────────
 * Nothing typed into the funnel is remembered across a page load. This gate used
 * to do the opposite — read `GET /bff/funnel` and restore the resolved meter so a
 * refresh kept the shopper's work. That is now unwanted: every load starts over.
 * So the gate's one blocking call is the session mint with `fresh=1`, which
 * retires the session the cookie named and gives this load an empty one; the
 * snapshot is deliberately NOT read, because after a fresh mint there is nothing
 * in it, and reading it BEFORE the mint would restore exactly what must not be.
 *
 * ── THE ONE EXEMPTION: AN OFFER ARRIVAL (2026-09-18) ───────────────────────
 * ⚠️ The rule above, applied to EVERY load without exception, broke every emailed
 * offer link. `/bff/offer/:token` mints a session server-side, seeds the
 * customer's meter, usage and identity into it, and redirects here — and this
 * gate then deleted precisely that, leaving the shelf with no meter and bouncing
 * the customer to the storefront home page. Verified end to end: the BFF logged
 * `offer entry: session seeded`, 302'd to the shelf, the SPA called
 * `session?fresh=1` 0.2s later, and `GET /bff/funnel` then reported
 * `candidate: null`.
 *
 * So an arrival carrying `STOREFRONT_OFFER_ARRIVAL_PARAM` does two things
 * differently, and ONLY those two: it does not retire the session, and it DOES
 * read the snapshot. Both are consistent with 2026-09-16 rather than exceptions
 * to it — that decision is about not restoring a PREVIOUS VISIT, and this
 * session was created by this navigation and holds only what the agent put in
 * the offer. A plain reload of the shelf carries no marker and still starts over.
 *
 * ── WHY STILL A GATE ───────────────────────────────────────────────────────
 * The mint must land before any step can act: a panel that fired `GET /bff/funnel`
 * or a lead POST against the OLD cookie would read or extend the old session. Gating
 * means the provider and every child mount with the new cookie already set.
 *
 * ── SUCCESS AND FAILURE BOTH RENDER THE CHILDREN ───────────────────────────
 * A refused mint (rate-limited, offline) must not put an error screen on the
 * storefront's front door; the children render and the meter sub-flows report their
 * own session errors as they always have. Only the PENDING state blocks.
 *
 * URL-held usage (`?usage=`/`?tier=`) is still carried into the seed: it is the
 * link's content, not a memory of a previous visit.
 */
export interface FunnelHydrationGateProps {
  readonly children: ReactNode;
}

export function FunnelHydrationGate({ children }: FunnelHydrationGateProps): JSX.Element {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // ── DID THIS LOAD ARRIVE FROM AN OFFER LINK? ───────────────────────────────
  // If so the BFF has, moments ago, minted this session and seeded the meter,
  // usage and identity into it — so the two things this gate normally does are
  // both wrong here: retiring the session throws the offer away, and skipping
  // the snapshot leaves the shelf with no meter to render. Doing either sent the
  // customer to the storefront home page, which is what every emailed offer link
  // did until 2026-09-18. See `STOREFRONT_OFFER_ARRIVAL_PARAM`.
  const fromOffer = searchParams.get(STOREFRONT_OFFER_ARRIVAL_PARAM) === '1';
  // E-CRM04 — the same exemption for a "resume your enrollment" arrival:
  // `GET /bff/resume/:token` minted and seeded this session moments ago (the
  // lead's saved meter, contact and proven email). Retiring it would throw the
  // saved progress away. The snapshot also carries `resumed: true`, which is what
  // shows the welcome-back banner. Not a control — see the contract param.
  const fromResume = searchParams.get(STOREFRONT_RESUME_ARRIVAL_PARAM) === '1';

  /* ── DEV-ONLY: KEEP THE SESSION ACROSS A RELOAD (2026-09-21) ───────────────
   * On a dev server the rule above is unworkable. Vite full-reloads the page on
   * many edits; each reload mints `session?fresh=1`, retires the session, and
   * the `candidate` already in the URL stops resolving — so the next step
   * refuses with "Resolve a service address before …", which reads as a product
   * bug and is really just the dev server reloading under the tester.
   *
   * With `VITE_DEV_FUNNEL_RESUME=true` this load behaves exactly like an offer
   * arrival: keep the session, read the snapshot, meter only. It is a dev-build
   * flag (`env.ts` ANDs it with `import.meta.env.DEV`), it is not set in the
   * committed `.env.development`, and production is untouched. */
  const resumeLocally = env.devFunnelResume;
  const keepSession = fromOffer || fromResume || resumeLocally;

  const session = useEnsureSessionQuery({ fresh: !keepSession });
  // Asked ONLY on an offer arrival, and only once the session call has settled —
  // reading it first would race the mint and could read the retired session.
  const snapshot = useFunnelSnapshotQuery({ enabled: keepSession && !session.isPending });

  const search = searchParams.toString();
  const snapshotData = snapshot.data;
  const seed = useMemo(
    () =>
      buildFunnelSeed({
        // `undefined` on an ordinary load keeps 2026-09-16 exactly as it was.
        snapshot: keepSession ? snapshotData : undefined,
        params: new URLSearchParams(search),
        // ⚠️ THE METER, NOT THE STEP. The snapshot is read here for one reason —
        // to recover the premise the BFF seeded — and its `step` must not ride
        // along: it is captured at mount and never refreshed, so the step gate
        // would pin the customer to where they stood on arrival and bounce every
        // forward click back to the credit page. See `FunnelSeedArgs.meterOnly`.
        meterOnly: true,
      }),
    [keepSession, snapshotData, search],
  );

  // On an offer arrival the snapshot is part of hydration, so the gate waits for
  // it too — mounting the shelf before the meter lands would trip its own
  // "no meter" guard and redirect away before the data arrived.
  if (session.isPending || (keepSession && snapshot.isPending)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="funnel-hydrating">
        <Spinner size="lg" label={t('shop.funnel.hydrating')} />
      </div>
    );
  }

  return (
    <FunnelProvider seed={seed} hydrated>
      {children}
    </FunnelProvider>
  );
}
